import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';
import { BreadcrumbSchema } from '@/components/seo/SchemaMarkup';

const Specials = () => {
  // Offers schema for SEO
  const offersSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Fort Worth Dental Specials",
    "description": "Current dental specials and new patient offers at Fort Worth Dental",
    "itemListElement": [
      {
        "@type": "Offer",
        "position": 1,
        "name": "$99 Emergency Exam and X-Ray",
        "description": "Emergency dental exam with X-ray for just $99",
        "price": "99",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": "https://fortworthdental.com/99-emergency-exam-and-x-ray-affordable-dental-services"
      },
      {
        "@type": "Offer",
        "position": 2,
        "name": "FREE Dental Implant Consultation",
        "description": "Free dental implant consultation with 3D CT scan",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": "https://fortworthdental.com/dental-implants-free-consult-with-x-rays"
      },
      {
        "@type": "Offer",
        "position": 3,
        "name": "FREE Invisalign Consultation",
        "description": "Free Invisalign consultation and smile preview",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": "https://fortworthdental.com/invisalign-free-consultation"
      },
      {
        "@type": "Offer",
        "position": 4,
        "name": "$199 Healthy Cleaning, Exam and X-Rays",
        "description": "Complete dental cleaning, exam and X-rays for new patients",
        "price": "199",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": "https://fortworthdental.com/179-healthy-cleaning-exam-and-set-of-x-rays"
      },
      {
        "@type": "Offer",
        "position": 5,
        "name": "FREE First Fit Veneers Consultation",
        "description": "Free consultation for First Fit Veneers - a brand-new smile in as little as 2 hours",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "url": "https://fortworthdental.com/first-fit-veneers"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Dental Specials Fort Worth TX | New Patient Offers | Fort Worth Dental</title>
        <meta name="description" content="Save on dental care with our Fort Worth specials. $99 emergency exam, FREE dental implant consultation, FREE Invisalign consultation, $199 cleaning special. Call (817) 985-3337." />
        <meta name="keywords" content="dental specials fort worth, dental coupons, new patient specials, cheap dentist fort worth tx, affordable dental care, dental discounts, free dental consultation" />
        <link rel="canonical" href="https://fortworthdental.com/specials" />
        <script type="application/ld+json">{JSON.stringify(offersSchema)}</script>
      </Helmet>
      
      {/* Schema.org Structured Data */}
      <BreadcrumbSchema items={[
        { name: "Home", url: "https://fortworthdental.com/" },
        { name: "Specials", url: "https://fortworthdental.com/specials" }
      ]} />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Special Offers</h1>
          <p className="text-xl">Take advantage of our new patient specials in Fort Worth</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          
          {/* Emergency Exam and X-Ray Section */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  $99 Emergency Exam and X-Ray
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  The best dental problem is the one that you never had! By following proper oral hygiene, brushing, 
                  flossing, proper diet and regular visits to your dentist at <strong>Fort Worth Dental</strong>, you can stay healthy. 
                  But when an emergency arises, Fort Worth Dental is here for you.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Fort Worth Dental is a general dental practice, we have helped thousands of dental patients and their 
                  families with general dental, cosmetic dentistry, dental implants and emergency dental needs.
                </p>
                <Link 
                  to="/emergency-dentist" 
                  className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
                >
                  Learn More About Emergency Dental
                </Link>
              </div>
              <div>
                {/* YouTube Video */}
                <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/phPxGtax2LI"
                    title="Fort Worth Dentist Office Tour"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonial Section 1 */}
          <section className="mb-16 bg-gray-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">What Others Are Saying</h3>
            <blockquote className="text-gray-700 text-lg italic mb-4">
              "My husband and I have been going to Fort Worth Dental for over 20 years. Our friends go to fancy Dallas 
              dentists but Dr. Razo and his team keep us coming back. Friendly, professional, on time, and Dr. Razo 
              keeps up with the latest in dental science. I highly recommend this practice!"
            </blockquote>
            <p className="text-teal-600 font-semibold">— Janis M.</p>
          </section>

          {/* Healthy Cleaning Section */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="order-2 lg:order-1">
                {/* YouTube Video */}
                <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/phPxGtax2LI"
                    title="Fort Worth Dental Office"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  $199 Healthy Cleaning, Exam and set of X-Rays*
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  What is the secret for avoiding costly and painful dental work? The secret is preventative dental 
                  practices. By following proper oral hygiene, brushing, flossing, proper diet and regular visits 
                  to your dentist at Fort Worth Dental, you can stay healthy.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Fort Worth Dental is a general dental practice, we have helped thousands of dental patients and 
                  their families with general dental, cosmetic dentistry, dental implants and emergency dental needs. 
                  Call today for an appointment at (817) 985-3337.
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  *In absence of periodontal disease
                </p>
                <Link 
                  to="/services/preventative-dentistry" 
                  className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
                >
                  Learn More About Preventative Care
                </Link>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="mb-16 bg-gradient-to-r from-teal-600 to-teal-700 text-white p-12 rounded-lg text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Schedule Your Appointment?</h2>
            <p className="text-xl mb-8">Call now or book online to take advantage of our special offers</p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <a 
                href="tel:8179853337" 
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors"
              >
                Call (817) 985-3337
              </a>
              <Link 
                to="/#book-appointment" 
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700 transition-colors"
              >
                Book Online
              </Link>
            </div>
          </section>

          {/* Testimonial Section 2 - Different testimonial */}
          <section className="mb-16 bg-gray-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">What Others Are Saying</h3>
            <blockquote className="text-gray-700 text-lg italic mb-4">
              "I've been a patient at Fort Worth Dental for years and have always received excellent care. 
              The staff is incredibly friendly and professional, and Dr. Mund did an amazing job with my dental implants. 
              I couldn't be happier with my smile!"
            </blockquote>
            <p className="text-teal-600 font-semibold">— Robert T.</p>
          </section>

          {/* More Specials Cards */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">More Special Offers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* First Fit Veneers - Featured */}
              <div className="bg-white border-2 border-purple-300 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow md:col-span-2">
                <div className="bg-gradient-to-r from-purple-700 to-teal-600 text-white p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-yellow-400 text-purple-900 px-3 py-1 rounded-full text-xs font-bold uppercase">New Service</span>
                  </div>
                  <h3 className="text-2xl font-bold">First Fit Veneers — FREE Consultation</h3>
                  <p className="text-purple-100 mt-1">A Brand-New Smile in as Little as 2 Hours</p>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 mb-4">
                    First Fit Veneers are the most innovative dental procedure available today. Using advanced digital technology, 
                    our certified doctors can give you a stunning, natural-looking smile in a single visit — no multiple appointments, 
                    no temporary veneers, no waiting weeks. Dr. Razo, Dr. Hiwiadi, and Dr. Pham are all First Fit certified.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm">Same-Day Results</span>
                    <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm">Digital Precision</span>
                    <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm">Minimally Invasive</span>
                    <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm">3 Certified Doctors</span>
                  </div>
                  <Link 
                    to="/first-fit-veneers" 
                    className="inline-block bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                  >
                    Schedule FREE Consultation
                  </Link>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-teal-600 text-white p-4">
                  <h3 className="text-xl font-bold">Dental Implants Free Consult with X-Rays</h3>
                  <p className="text-yellow-300 font-semibold">$475 Value - FREE!</p>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 mb-4">
                    Fort Worth Dental is now offering FREE CONSULTATION plus X-Rays for new dental implant patients.
                  </p>
                  <Link 
                    to="/specials/dental-implants-free-consult" 
                    className="text-teal-600 font-semibold hover:underline"
                  >
                    Learn More →
                  </Link>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-purple-600 text-white p-4">
                  <h3 className="text-xl font-bold">Invisalign® - Free Consultation</h3>
                  <p className="text-yellow-300 font-semibold">Clear Aligners for Adults & Teens</p>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 mb-4">
                    Fort Worth Dental is now offering FREE CONSULTATION for Invisalign® clear aligners.
                  </p>
                  <Link 
                    to="/invisalign" 
                    className="text-purple-600 font-semibold hover:underline"
                  >
                    Learn More →
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
              Schedule Your Appointment
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Fill out the form below or call us at <a href="tel:8179853337" className="text-teal-600 font-semibold">(817) 985-3337</a>
            </p>
            <AppointmentForm />
          </section>

        </div>
      </div>
    </div>
  );
};

export default Specials;
